import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import axios from "axios";
import { getApiBaseUrl } from "../config/env";

/**
 * Backoffice now authenticates via the HttpOnly `eriol_session_admin` cookie
 * set by the backend at /admin/login. `withCredentials: true` is what makes
 * axios attach the cookie on cross-origin calls to api.eriol.io; CORS
 * Allow-Credentials is already on server-side.
 *
 * The Authorization header path (and the local accessToken module variable)
 * is gone — there's no token for JS to read or store. The browser owns it.
 */

export const SESSION_EXPIRED_EVENT = "backoffice:session-expired";

export const onSessionExpired = (callback: () => void): (() => void) => {
  window.addEventListener(SESSION_EXPIRED_EVENT, callback);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, callback);
};

const SAFE_METHODS = new Set(["get", "head", "options"]);

/**
 * Read the non-HttpOnly `eriol_csrf` cookie the backend issues alongside the
 * session cookie. We echo it back as `X-CSRF-Token` on every mutating
 * request — the backend's CSRF middleware rejects (403) cookie-authed
 * mutations that don't carry the matching header value.
 */
function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)eriol_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const onRequest = (config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? "get").toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) {
      config.headers = config.headers ?? {};
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: 1800000, // 30 minutes for large file uploads
});

apiClient.interceptors.request.use(onRequest);

// Response interceptor for 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && typeof window !== "undefined") {
      const url = error.config?.url ?? "";
      // A 401 from these endpoints does NOT mean the admin session expired, so
      // they must not nuke the session:
      //   - /admin/login: just wrong credentials.
      //   - /me/push-subscriptions: endpoint LEGADO. El backoffice ya no lo
      //     usa (ver pushApi.ts), y sigue exceptuado solo por compatibilidad
      //     mientras dure la migración: una pestaña vieja todavía abierta lo
      //     llama. No es que ahí un admin no exista —  PushController resuelve
      //     sesiones de admin Y de usuario— ; el problema es que AccessKeyGuard
      //     elige la credencial por el prefijo de la URL, así que fuera de
      //     `/admin` prefiere la cookie de usuario, y cuando esa no sirve el
      //     401 no dice nada sobre la sesión de admin. Tratarlo como "sesión
      //     vencida" deslogueaba al admin en cualquier pantalla que montara el
      //     hook de push (ajustes, crons, …).
      //
      // OJO: `/admin/push-subscriptions` NO entra acá. Esa ruta exige sesión de
      // admin, así que un 401 suyo SÍ significa que la sesión venció y tiene
      // que disparar el flujo normal.
      const sessionIrrelevant =
        url.includes("/admin/login") || url.includes("/me/push-subscriptions");
      if (!sessionIrrelevant) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
